// Renders an individual user chat message
import React from 'react';
import { Message, ChatFile } from '../../types/chat';
import { MessageActions } from '../message-actions';
import { formatTimestamp } from '../../lib/chatUtils';
import { useChatContext } from '@/contexts/ChatContext';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { normalizeMessageContentWithProfile, userMessageProfile } from '@/lib/messageContent';
import { createMarkdownComponents, MarkdownRenderer } from './markdown/primitives';
import { userMessageMarkdownShell, userMessageMarkdownTable, userMessageMarkdownTypography } from './markdown/tokens';
import { ChatAttachmentList, ChatBubble, ChatBubbleHeader, ChatReplySnippet } from './message-primitives';

interface UserMessageProps {
  message: Message;
  files: ChatFile[];
  scrollToMessage: (messageId: string) => void;
}

export const UserMessage: React.FC<UserMessageProps> = ({
  message,
  files,
  scrollToMessage,
}) => {
  const {
    activeChat,
    isResponding,
    handleEditMessage,
    handleReplyToMessage,
    messageHoverStates,
    handleMessageHover,
    isEditingMessage,
    messageBeingEdited,
    editedMessageContent,
    setEditedMessageContent,
    handleSaveEdit,
    handleCancelEdit,
  } = useChatContext();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [copyStatus, setCopyStatus] = React.useState<'idle' | 'success' | 'error'>('idle');
  const noop = React.useCallback(() => { }, []);

  const isHovering = messageHoverStates[message.id] || false;
  const isBeingEdited = isEditingMessage && messageBeingEdited?.id === message.id;

  // Find the message being replied to, if any
  const repliedToMessage = message.replyToMessageId && activeChat
    ? activeChat.messages.find((m: Message) => m.id === message.replyToMessageId)
    : undefined;

  const messageFiles = message.fileIds
    ?.map(fileId => files.find(f => f.id === fileId))
    .filter((file): file is ChatFile => !!file) || [];

  React.useEffect(() => {
    if (copyStatus !== 'idle') {
      const timer = setTimeout(() => setCopyStatus('idle'), 2000);
      return () => clearTimeout(timer);
    }
  }, [copyStatus]);

  const onCopy = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(message.content);
        setCopyStatus('success');
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = message.content;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (success) {
          setCopyStatus('success');
        } else {
          throw new Error('Fallback copy failed');
        }
      }
    } catch (err) {
      console.error('Copy failed:', err);
      setCopyStatus('error');
    }
  };

  const displayContent = React.useMemo(() => {
    return normalizeMessageContentWithProfile(message.content, userMessageProfile);
  }, [message.content]);
  const markdownComponents = React.useMemo(
    () => createMarkdownComponents({
      typography: userMessageMarkdownTypography,
      table: userMessageMarkdownTable,
      t,
      renderLink: ({ children, ...props }) => (
        <a
          {...props}
          className="text-white hover:text-neutral-100 underline break-words"
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      ),
    }),
    [t]
  );

  return (
    <ChatBubble
      messageId={message.id}
      align="end"
      bubbleClassName={`p-3 text-white ${isMobile ? 'bg-neutral-800/50' : 'bg-black'}`}
      actionsClassName="absolute bottom-full mb-1 right-0 flex items-center space-x-1 z-10"
      onMouseEnter={() => handleMessageHover(message.id, true)}
      onMouseLeave={() => handleMessageHover(message.id, false)}
      header={(
        <ChatBubbleHeader
          label={t('you')}
          timestamp={formatTimestamp(message.timestamp)}
        />
      )}
      replySnippet={repliedToMessage ? (
        <ChatReplySnippet
          authorLabel={`${t('replying_to')} ${repliedToMessage.role === 'user' ? t('you') : 'Lema'}`}
          content={repliedToMessage.content}
          onClick={() => scrollToMessage(repliedToMessage.id)}
          title={t('jump_to_original')}
        />
      ) : undefined}
      body={isBeingEdited ? (
        <textarea
          value={editedMessageContent}
          onChange={(e) => setEditedMessageContent(e.target.value)}
          className="w-full p-2 my-1 border rounded-md text-sm text-black dark:text-white bg-white dark:bg-neutral-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
          rows={Math.max(3, message.content.split('\n').length)}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSaveEdit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              handleCancelEdit();
            }
          }}
        />
      ) : (
        <MarkdownRenderer
          content={displayContent}
          components={markdownComponents}
          shell={userMessageMarkdownShell}
        />
      )}
      attachments={(
        <ChatAttachmentList
          files={messageFiles}
          itemClassName="bg-neutral-300"
          iconClassName="text-neutral-700"
          textClassName="text-neutral-900"
        />
      )}
      actions={isHovering && !isBeingEdited ? (
        <MessageActions
          messageId={message.id}
          isAssistant={false}
          isLast={false}
          isEditing={isBeingEdited}
          isHovering={isHovering}
          onCopy={onCopy}
          copyStatus={copyStatus}
          onEdit={() => handleEditMessage(message)}
          onReply={() => handleReplyToMessage(message)}
          onRegenerate={noop}
          showActions={!isResponding}
          onLike={noop}
          onDislike={noop}
          feedback={message.feedback}
          isStreaming={false}
        />
      ) : undefined}
    />
  );
};
